/* contains utility functions to use along side the address input form
 * e.g. function that attempts to geocode an address
 */

import { strIsNullOrEmpty } from '@fox/shared';

import { AddressStateType } from './types';

type AddressSelectors = {
	[x: string]: {
		selector: string | string[];
		defaultName: string;
	};
};

const addressSelectors: AddressSelectors = {
	building: { selector: 'premise', defaultName: 'long_name' },
	unit: {
		selector: 'subpremise',
		defaultName: 'long_name'
	},
	number: {
		selector: 'street_number',
		defaultName: 'long_name'
	},
	locality: {
		selector: ['sublocality', 'locality'], // This reads as: Prefer 'sublocality' if available, otherwise use 'locality'
		defaultName: 'long_name'
	},
	country: {
		selector: 'country',
		defaultName: 'short_name'
	},
	postcode: {
		selector: 'postal_code',
		defaultName: 'short_name'
	},
	streetName: {
		selector: 'route',
		defaultName: 'long_name'
	},
	state: {
		selector: 'administrative_area_level_1',
		defaultName: 'short_name'
	}
};

/** is being used in other address components */
export const getAddressModel = (res: google.maps.GeocoderAddressComponent[], formatted_address: string) => {
	const tempData: Record<string, string> = { formatted: formatted_address };
	const applied = new Set<string>();
	Object.keys(addressSelectors).forEach(k => {
		for (const comp of res) {
			const s = addressSelectors[k].selector;
			if (Array.isArray(s)) {
				for (let i = 0; i < s.length; i++) {
					if (comp.types.includes(s[i])) {
						if (!applied.has(k)) {
							tempData[k] = comp[addressSelectors[k].defaultName];
							applied.add(k);
						}
						break; // found the first match, no need to continue
					}
				}
			} else {
				if (comp.types.includes(s)) {
					if (!applied.has(k)) {
						tempData[k] = comp[addressSelectors[k].defaultName];
						applied.add(k);
					}
				}
			}
		}
	});

	const address = mergeStreetAddress(tempData);

	// try to work out the building name based on the result - it is present in formatted address but not in any of the individual components (ugh)
	// if (!address.building) {
	// 	const mightBeBuilding = formatted_address.split(',')[0];
	// 	if (mightBeBuilding != address.address1 && !res.some(a => a.long_name === mightBeBuilding || a.short_name === mightBeBuilding)) {
	// 		address.building = mightBeBuilding;
	// 	}
	// }

	return address;
};

/** merges unit, number and streetName  */
export function mergeStreetAddress(address): AddressStateType {
	const { unit, number, streetName, ...rest } = address;
	const newAddress = {
		...rest,
		number,
		streetName,
		address1: formatStreetAddress({ unit, number, streetName })
	};
	return newAddress;
}

type FormatStreetAddressInput = {
	unit?: string;
	number?: string;
	streetName?: string;
};

/** merges unit, number and streetName  */
export function formatStreetAddress({ unit, number, streetName }: FormatStreetAddressInput) {
	return [unit ? [unit, number].join('/') : number, streetName].filter(a => !strIsNullOrEmpty(a)).join(' ');
}

/** formats the address into a string that is used in geocoding*/
export function addressStateToString(addressState?: AddressStateType | null | undefined) {
	if (!addressState) {
		return '';
	}
	const { building, address1, locality, postcode, state, country } = addressState;
	return [building ? building + ',' : '', address1, locality, state, postcode, country].filter(a => !strIsNullOrEmpty(a)).join(' ');
}

/** fills in any blanks, does not touch the fields that are already filled */
export function autoFillAddressState(addressState: AddressStateType, suggestion: AddressStateType): AddressStateType {
	const merged = {};
	for (const key in addressState) {
		merged[key] = addressState[key] === undefined || addressState[key] === null || addressState[key] === '' ? suggestion[key] : addressState[key];
	}
	return merged;
	// // We cannot use this V because some fields might be empty string
	// return Object.assign({}, suggestion, addressState);
}

/** empty  */
export const EMPTY_ADDRESS_STATE: AddressStateType = {
	address1: '',
	building: '',
	locality: '',
	postcode: '',
	state: '',
	country: '',
	formatted: ''
};

/** turns google's format into AddressStateType */
export function formatGeocodeResult(res: google.maps.GeocoderResult | undefined): AddressStateType | null {
	if (!res) {
		return null;
	}
	const addressProps = getAddressModel(res.address_components, res.formatted_address);

	const lat = res.geometry.location.lat();
	const lng = res.geometry.location.lng();

	return {
		...EMPTY_ADDRESS_STATE,
		lat,
		lng,
		...addressProps,
		formatted: res.formatted_address ?? addressStateToString(addressProps)
	};
}

/** calls formatGeocodeResult on each of the geocode results google returns*/
export function formatGeocodeResultList(res: google.maps.GeocoderResult[]): Array<AddressStateType> {
	return (res?.map(r => formatGeocodeResult(r)) ?? []).filter(r => r !== null);
}

/** calls google's api to search for a location */
/** we probably still want to do this on selecting an address right? */
export function geocodeAsync(
	address: string,
	region: string
): Promise<{ res: google.maps.GeocoderResult[] | null; status: google.maps.GeocoderStatus }> {
	if (strIsNullOrEmpty(address)) {
		return Promise.resolve({ status: google.maps.GeocoderStatus.INVALID_REQUEST, res: [] });
	}
	const geocoder = new google.maps.Geocoder();
	return new Promise((resolve, reject) => {
		geocoder.geocode({ address, region: region }, (res, status) => {
			resolve({ res, status });
		});
	});
}

/** geo codes an object of AddressStateType */
export function geoCodeAddressStateAsync(addressState: AddressStateType, region: string) {
	return geocodeAsync(addressStateToString(addressState), region);
}

/** list of address locations found through google */
export async function geocodeListAsync(addressState: AddressStateType, region: string) {
	const { res, status } = await geoCodeAddressStateAsync(addressState, region);
	// if geocode found return geocoded obj
	if (status === google.maps.GeocoderStatus.OK && res) {
		return formatGeocodeResultList(res);
	} else {
		// geo code not found
		return [];
	}
}

/** assumes first choice google presents is correct address, hence naive
 * returns new address obj w geo code if geo code found, else return original obj
 */
export async function naiveAttemptGeocodeAsync(addressState: AddressStateType, region: string, replaceCoordinates = false) {
	const formattedResults = await geocodeListAsync(addressState, region);
	// if no results just return original obj
	if (!formattedResults.length) {
		return addressState;
	}
	// we assume it is the first choice
	// console.log('geocode found', formattedResults[0]);
	const newAddress = autoFillAddressState(addressState, formattedResults[0]);
	// console.log('new', newAddress);
	return replaceCoordinates ? { ...newAddress, lat: formattedResults[0].lat, lng: formattedResults[0].lng } : newAddress;
}

/**country is defaulted to Australia */
export const buildDefaultEditAddressState = (country: string) => ({ ...EMPTY_ADDRESS_STATE, country: country, formatted: country });

/** To distinguish between AddressStateTypeBase and GoogleAddressType
 * I hate this
 */
export function isGoogleAddressType(addr) {
	return !!addr?.formatted;
}

export function shouldAttemptGeocode(newState: AddressStateType) {
	const bHasEnoughComponents =
		!strIsNullOrEmpty(newState.address1) && (!strIsNullOrEmpty(newState.postcode) || !strIsNullOrEmpty(newState.locality));
	return newState.lat == null && newState.lng == null && bHasEnoughComponents;
}
